import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  contentChild,
  input,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faLanguage } from "@fortawesome/free-solid-svg-icons";
import { TranslateModule } from "@ngx-translate/core";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";

import { TranslatableFieldBase } from "./translatable-field.base";

/**
 * Presentational shell for translatable Formly fields. Renders the
 * label, per-locale chip row, translate split-button dropdown, body
 * wrapper, and error message. The editor element (input, ckeditor, …)
 * is projected as an `<ng-template>` child that receives the active
 * locale code as `$implicit`.
 */
@Component({
  selector: "app-translatable-shell",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    FontAwesomeModule,
    BsDropdownModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let f = field();
    <div class="flex flex-col gap-2" [attr.data-field-id]="f.field.id">
      @if (f.props.label; as label) {
      <span
        class="inline-block font-alternate text-xs font-semibold tracking-wider uppercase text-[var(--mat-sys-on-surface-variant)]"
      >
        {{ label | translate }}
        @if (f.props['requireOneLocale']) {
        <span class="text-mty-danger ml-0.5">*</span> }
      </span>
      }

      <div class="flex flex-wrap gap-1.5 mb-2.5 items-center">
        @for (lang of f.languages; track lang.code) {
        <button
          type="button"
          [class]="f.chipClass(lang.code)"
          [attr.aria-pressed]="f.activeTab() === lang.code"
          (click)="f.setActiveTab(lang.code)"
        >
          {{ lang.code | uppercase }}
          @if (f.isLocaleEmpty(lang.code)) {
          <span
            class="inline-block w-1.5 h-1.5 rounded-full bg-mty-danger"
            aria-label="empty translation"
          ></span>
          }
        </button>
        } @if (f.props['showTranslateButton'] !== false) {
        <div
          class="ml-auto btn-group"
          dropdown
          [isDisabled]="!f.hasSourceContent() || f.isTranslating()"
        >
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            [disabled]="!f.hasSourceContent() || f.isTranslating()"
            (click)="f.translateInline()"
            [title]="'Translate to selected languages' | translate"
          >
            <fa-icon [icon]="faLanguage" class="mr-1"></fa-icon>
            <span>{{
              (f.isTranslating() ? "Translating..." : "Translate") | translate
            }}</span>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary dropdown-toggle dropdown-toggle-split"
            dropdownToggle
            [disabled]="!f.hasSourceContent() || f.isTranslating()"
            [title]="'Select target languages' | translate"
          >
            <span class="visually-hidden sr-only">{{
              "Toggle Dropdown" | translate
            }}</span>
          </button>
          <ul *dropdownMenu class="dropdown-menu dropdown-menu-right">
            @for (lang of f.targetLanguages(); track lang.code) {
            <li>
              <label
                class="dropdown-item mb-0 d-flex align-items-center cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="mr-2"
                  [ngModel]="lang.selected"
                  (ngModelChange)="f.setLocaleSelected(lang.code, $event)"
                  (click)="$event.stopPropagation()"
                />
                {{ lang.label }}
              </label>
            </li>
            }
          </ul>
        </div>
        }
      </div>

      <div [class]="f.bodyClass()">
        @for (lang of f.languages; track lang.code) { @if (f.activeTab() ===
        lang.code) {
        <ng-container
          *ngTemplateOutlet="editor(); context: { $implicit: lang.code }"
        ></ng-container>
        } }
      </div>

      @if (f.isInvalid()) {
      <span class="font-base text-[12px] text-mty-danger">
        {{ "This field is required" | translate }}
      </span>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .dropdown-item {
        cursor: pointer;
      }
      .dropdown-item:active {
        background-color: inherit;
        color: inherit;
      }
    `,
  ],
})
export class TranslatableShellComponent {
  readonly field = input.required<TranslatableFieldBase>();
  readonly editor = contentChild.required(TemplateRef);
  readonly faLanguage = faLanguage;
}
